import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/Users/bradwestfall/Desktop/ReactTraining/Projects/monorepo/reacttraining.com/src/page-templates/BlogArticlePage.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <p>{`Today, we are releasing `}<a parentName="p" {...{
        "href": "https://github.com/ReactTraining/react-router/releases/tag/v5.1.0"
      }}>{`React Router version 5.1`}</a>{`. This post will discuss the 5.1 release as well as discuss a few things you can do to prepare for the future.`}</p>
    <p>{`We are hard at work incorporating the best ideas from `}<a parentName="p" {...{
        "href": "https://github.com/reach/router"
      }}>{`@reach/router`}</a>{`, as well as community feedback about the 4/5 API, into version 6 which we expect will be ready sometime around the beginning of the new year. That being said, we are putting a lot of emphasis on making a silky smooth migration path for anyone who wants to stay up to date as we get closer to v6. As we introduce new features and APIs, we will let you know at every step of the way what you can do to make the transition as easy as possible.`}</p>
    <h2 {...{
      "id": "meet-react-router-51"
    }}><a parentName="h2" {...{
        "href": "#meet-react-router-51",
        "aria-hidden": "true",
        "tabIndex": -1
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`Meet React Router 5.1`}</h2>
    <p>{`Easily the most notable feature in this release is the addition of some hooks (for React 16.8 users, ofc). We are excited about the ability that hooks give us to `}<strong parentName="p">{`compose state and behavior`}</strong>{` instead of just composing elements.`}</p>
    <p>{`At its heart, React Router is a state container for the current `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`location`}</code>{`, or URL. It keeps track of the `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`location`}</code>{` and renders different `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Route>`}</code>{`s as it changes, and it also gives you tools to update the `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`location`}</code>{` using `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Link>`}</code>{`s and the `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`history`}</code>{` API. Given the fact that managing this piece of state is the router's main responsibility, you can imagine that a new primitive that lets us compose state is going to enable a few things we couldn't do before!`}</p>
    <p>{`Let's explore the new hooks 1 by 1, and we'll follow it up with some tips about how to get the most out of this release while getting ready for the future of React Router.`}</p>
    <h3 {...{
      "id": "useparams"
    }}><a parentName="h3" {...{
        "href": "#useparams",
        "aria-hidden": "true",
        "tabIndex": -1
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a><code parentName="h3" {...{
        "className": "language-text"
      }}>{`useParams`}</code></h3>
    <p>{`First up is `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`useParams`}</code>{`. In React Router, we use the word "params" to describe dynamic segments of the URL. Before now, if you wanted to access the current params, you had to get them either through the `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`match`}</code>{` prop passed to your `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`component`}</code>{` or through the arguments to our `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`render`}</code>{` prop, like this:`}</p>
    <div {...{
      "className": "gatsby-highlight",
      "data-language": "jsx"
    }}><pre parentName="div" {...{
        "className": "language-jsx"
      }}><code parentName="pre" {...{
          "className": "language-jsx"
        }}><span parentName="code" {...{
            "className": "token keyword"
          }}>{`import`}</span>{` React `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`from`}</span>{` `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'react'`}</span>{`
`}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`import`}</span>{` ReactDOM `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`from`}</span>{` `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'react-dom'`}</span>{`
`}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`import`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{` BrowserRouter `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`as`}</span>{` Router`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{` Route`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{` Switch `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{` `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`from`}</span>{` `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'react-router-dom'`}</span>{`

`}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// In the code below, BlogPost is used as both a <Route component>`}</span>{`
`}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// and in a <Route render> function. In both cases, it receives a \`match\``}</span>{`
`}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// prop, which it uses to get the URL params.`}</span>{`
`}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`function`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`BlogPost`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token parameter"
          }}><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`{`}</span>{` match `}<span parentName="span" {...{
              "className": "token punctuation"
            }}>{`}`}</span></span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`let`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{` slug `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{` `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span>{` match`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`.`}</span>{`params
  `}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// ...`}</span>{`
`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{`

ReactDOM`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`.`}</span><span parentName="code" {...{
            "className": "token function"
          }}>{`render`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span>{`
  `}<span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Router`}</span></span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
    `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span>{`div`}</span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
      `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Switch`}</span></span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
        `}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span><span parentName="code" {...{
            "className": "token comment"
          }}>{`/* Using the \`component\` prop */`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
        `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Route`}</span></span>{` `}<span parentName="span" {...{
              "className": "token attr-name"
            }}>{`path`}</span><span parentName="span" {...{
              "className": "token attr-value"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`=`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`"`}</span>{`/blog/:slug`}<span parentName="span" {...{
                "className": "token punctuation"
              }}>{`"`}</span></span>{` `}<span parentName="span" {...{
              "className": "token attr-name"
            }}>{`component`}</span><span parentName="span" {...{
              "className": "token script language-javascript"
            }}><span parentName="span" {...{
                "className": "token script-punctuation punctuation"
              }}>{`=`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`{`}</span>{`BlogPost`}<span parentName="span" {...{
                "className": "token punctuation"
              }}>{`}`}</span></span>{` `}<span parentName="span" {...{
              "className": "token punctuation"
            }}>{`/>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`

        `}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span><span parentName="code" {...{
            "className": "token comment"
          }}>{`/* Using the \`render\` prop */`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
        `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Route`}</span></span>{`
          `}<span parentName="span" {...{
              "className": "token attr-name"
            }}>{`path`}</span><span parentName="span" {...{
              "className": "token attr-value"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`=`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`"`}</span>{`/posts/:slug`}<span parentName="span" {...{
                "className": "token punctuation"
              }}>{`"`}</span></span>{`
          `}<span parentName="span" {...{
              "className": "token attr-name"
            }}>{`render`}</span><span parentName="span" {...{
              "className": "token script language-javascript"
            }}><span parentName="span" {...{
                "className": "token script-punctuation punctuation"
              }}>{`=`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`{`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`(`}</span><span parentName="span" {...{
                "className": "token parameter"
              }}><span parentName="span" {...{
                  "className": "token punctuation"
                }}>{`{`}</span>{` match `}<span parentName="span" {...{
                  "className": "token punctuation"
                }}>{`}`}</span></span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`)`}</span>{` `}<span parentName="span" {...{
                "className": "token operator"
              }}>{`=>`}</span>{` `}<span parentName="span" {...{
                "className": "token tag"
              }}><span parentName="span" {...{
                  "className": "token tag"
                }}><span parentName="span" {...{
                    "className": "token punctuation"
                  }}>{`<`}</span><span parentName="span" {...{
                    "className": "token class-name"
                  }}>{`BlogPost`}</span></span>{` `}<span parentName="span" {...{
                  "className": "token attr-name"
                }}>{`match`}</span><span parentName="span" {...{
                  "className": "token script language-javascript"
                }}><span parentName="span" {...{
                    "className": "token script-punctuation punctuation"
                  }}>{`=`}</span><span parentName="span" {...{
                    "className": "token punctuation"
                  }}>{`{`}</span>{`match`}<span parentName="span" {...{
                    "className": "token punctuation"
                  }}>{`}`}</span></span>{` `}<span parentName="span" {...{
                  "className": "token punctuation"
                }}>{`/>`}</span></span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`}`}</span></span>{`
        `}<span parentName="span" {...{
              "className": "token punctuation"
            }}>{`/>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
      `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`</`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Switch`}</span></span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
    `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`</`}</span>{`div`}</span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
  `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`</`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Router`}</span></span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
  document`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`.`}</span><span parentName="code" {...{
            "className": "token function"
          }}>{`getElementById`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token string"
          }}>{`'root'`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{`
`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span></code></pre></div>
    <p>{`Both of these methods are fine for getting the current URL params, but they both leave something to be desired. In the case of the `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`component`}</code>{` prop, the router actually has to `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`createElement(component)`}</code>{` for you, which prevents you from passing in custom props to your `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<BlogPost>`}</code>{` element. In the case of the `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`render`}</code>{` prop, you can pass in custom props but you're forced to manually pass along the values you get from the callback to your element.`}</p>
    <p>{`In addition, `}<em parentName="p">{`both`}</em>{` methods only provide access to the params in `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<BlogPost>`}</code>{`, so you're forced to manually pass them along to components further down the tree if you need them.`}</p>
    <p>{`Let's see how `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`useParams`}</code>{` fixes all of these problems for us:`}</p>
    <div {...{
      "className": "gatsby-highlight",
      "data-language": "jsx"
    }}><pre parentName="div" {...{
        "className": "language-jsx"
      }}><code parentName="pre" {...{
          "className": "language-jsx"
        }}><span parentName="code" {...{
            "className": "token keyword"
          }}>{`import`}</span>{` React `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`from`}</span>{` `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'react'`}</span>{`
`}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`import`}</span>{` ReactDOM `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`from`}</span>{` `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'react-dom'`}</span>{`
`}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`import`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
  BrowserRouter `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`as`}</span>{` Router`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
  Route`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
  Switch`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
  useParams
`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{` `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`from`}</span>{` `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'react-router-dom'`}</span>{`

`}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`function`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`BlogPost`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
  `}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// We can call useParams() here to get the params,`}</span>{`
  `}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// or in any child element as well!`}</span>{`
  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`let`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{` slug `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{` `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`useParams`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{`
  `}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// ...`}</span>{`
`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{`

ReactDOM`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`.`}</span><span parentName="code" {...{
            "className": "token function"
          }}>{`render`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span>{`
  `}<span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Router`}</span></span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
    `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span>{`div`}</span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
      `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Switch`}</span></span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
        `}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span><span parentName="code" {...{
            "className": "token comment"
          }}>{`/* No weird props here, just use
            regular \`children\` elements! */`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
        `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Route`}</span></span>{` `}<span parentName="span" {...{
              "className": "token attr-name"
            }}>{`path`}</span><span parentName="span" {...{
              "className": "token attr-value"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`=`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`"`}</span>{`/posts/:slug`}<span parentName="span" {...{
                "className": "token punctuation"
              }}>{`"`}</span></span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
          `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`BlogPost`}</span></span>{` `}<span parentName="span" {...{
              "className": "token punctuation"
            }}>{`/>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
        `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`</`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Route`}</span></span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
      `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`</`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Switch`}</span></span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
    `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`</`}</span>{`div`}</span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
  `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`</`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Router`}</span></span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
  document`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`.`}</span><span parentName="code" {...{
            "className": "token function"
          }}>{`getElementById`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token string"
          }}>{`'root'`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{`
`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span></code></pre></div>
    <p>{`Notice 2 things:`}</p>
    <ul>
      <li parentName="ul">{`We don't have to do any weird component composition tricks to get the params, we can just use regular `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`children`}</code>{` elements`}</li>
      <li parentName="ul">{`We can `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`useParams()`}</code>{` anywhere in `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`<BlogPost>`}</code>{` or its subtree without manually passing params around`}</li>
    </ul>
    <p>{`There is also another really nice benefit here for TypeScript users: no more typing `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`component`}</code>{` and/or `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`render`}</code>{` props! You already know how to type regular React `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`children`}</code>{`.`}</p>
    <p>{`Of course, these are really just benefits of using hooks, so they'll apply to all of the hooks we discuss here. But it's really interesting to see the practical benefits of hooks in action (we are a `}<em parentName="p">{`training`}</em>{` company after all, so we kind of nerd out on this stuff 🤓)!`}</p>
    <h3 {...{
      "id": "uselocation"
    }}><a parentName="h3" {...{
        "href": "#uselocation",
        "aria-hidden": "true",
        "tabIndex": -1
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a><code parentName="h3" {...{
        "className": "language-text"
      }}>{`useLocation`}</code></h3>
    <p>{`Another hook in the 5.1 release is `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`useLocation`}</code>{`, which returns the current `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`location`}</code>{` object. This is useful any time you need to know the current URL.`}</p>
    <p>{`For example, imagine a `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`useEffect`}</code>{` hook where you want to send a "page view" event to your web analytics service every time the URL changes. With `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`useLocation`}</code>{`, it's as easy as:`}</p>
    <div {...{
      "className": "gatsby-highlight",
      "data-language": "js"
    }}><pre parentName="div" {...{
        "className": "language-js"
      }}><code parentName="pre" {...{
          "className": "language-js"
        }}><span parentName="code" {...{
            "className": "token keyword"
          }}>{`import`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{` Switch`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{` useLocation `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{` `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`from`}</span>{` `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'react-router-dom'`}</span>{`

`}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`function`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`usePageViews`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`let`}</span>{` location `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`useLocation`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{`

  `}<span parentName="code" {...{
            "className": "token function"
          }}>{`useEffect`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span>{`
    `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=>`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
      ga`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`.`}</span><span parentName="code" {...{
            "className": "token function"
          }}>{`send`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`[`}</span><span parentName="code" {...{
            "className": "token string"
          }}>{`'pageview'`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{` location`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`.`}</span>{`pathname`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`]`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{`
    `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
    `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`[`}</span>{`location`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`]`}</span>{`
  `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{`
`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{`

`}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`function`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`App`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
  `}<span parentName="code" {...{
            "className": "token function"
          }}>{`usePageViews`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{`
  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`return`}</span>{` `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`Switch`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span><span parentName="code" {...{
            "className": "token comment"
          }}>{`/* your routes here */`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span>{`Switch`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span></code></pre></div>
    <h3 {...{
      "id": "usehistory"
    }}><a parentName="h3" {...{
        "href": "#usehistory",
        "aria-hidden": "true",
        "tabIndex": -1
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a><code parentName="h3" {...{
        "className": "language-text"
      }}>{`useHistory`}</code></h3>
    <p>{`For programmatic navigation purposes, we provide access to the `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`history`}</code>{` object via `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`useHistory`}</code>{`.`}</p>
    <div {...{
      "className": "gatsby-highlight",
      "data-language": "jsx"
    }}><pre parentName="div" {...{
        "className": "language-jsx"
      }}><code parentName="pre" {...{
          "className": "language-jsx"
        }}><span parentName="code" {...{
            "className": "token keyword"
          }}>{`import`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{` useHistory `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{` `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`from`}</span>{` `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'react-router-dom'`}</span>{`

`}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`function`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`BackButton`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token parameter"
          }}><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`{`}</span>{` children `}<span parentName="span" {...{
              "className": "token punctuation"
            }}>{`}`}</span></span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`let`}</span>{` history `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`useHistory`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{`
  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`return`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span>{`
    `}<span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span>{`button`}</span>{` `}<span parentName="span" {...{
              "className": "token attr-name"
            }}>{`type`}</span><span parentName="span" {...{
              "className": "token attr-value"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`=`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`"`}</span>{`button`}<span parentName="span" {...{
                "className": "token punctuation"
              }}>{`"`}</span></span>{` `}<span parentName="span" {...{
              "className": "token attr-name"
            }}>{`onClick`}</span><span parentName="span" {...{
              "className": "token script language-javascript"
            }}><span parentName="span" {...{
                "className": "token script-punctuation punctuation"
              }}>{`=`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`{`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`(`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`)`}</span>{` `}<span parentName="span" {...{
                "className": "token operator"
              }}>{`=>`}</span>{` history`}<span parentName="span" {...{
                "className": "token punctuation"
              }}>{`.`}</span><span parentName="span" {...{
                "className": "token function"
              }}>{`goBack`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`(`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`)`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`}`}</span></span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
      `}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`children`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
    `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`</`}</span>{`button`}</span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span>{`
  `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{`
`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span></code></pre></div>
    <p>{`Heads up: The `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`useHistory`}</code>{` hook is a quick stopgap for a future hook that we are working on: `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`useNavigate`}</code>{`. `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`useNavigate`}</code>{` will provide an API that is more closely aligned with `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Link>`}</code>{` and will fix a few long-standing problems with using the `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`history`}</code>{` API directly in the router (it'll probably look a lot like `}<a parentName="p" {...{
        "href": "https://reach.tech/router/api/navigate"
      }}>{`@reach/router's `}<code parentName="a" {...{
          "className": "language-text"
        }}>{`navigate`}</code>{` API`}</a>{`). We are providing `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`useHistory`}</code>{` for now to make the migration of existing code that uses the `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`history`}</code>{` API as painless as possible.`}</p>
    <h3 {...{
      "id": "useroutematch"
    }}><a parentName="h3" {...{
        "href": "#useroutematch",
        "aria-hidden": "true",
        "tabIndex": -1
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a><code parentName="h3" {...{
        "className": "language-text"
      }}>{`useRouteMatch`}</code></h3>
    <p>{`The `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`useRouteMatch`}</code>{` hook is useful any time you are using a `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Route>`}</code>{` just so you can get access to its `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`match`}</code>{` data, including all of the times you might render a `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Route>`}</code>{` all by itself outside of a `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Switch>`}</code>{`. It matches the URL exactly like a `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Route>`}</code>{` would, including the `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`exact`}</code>{`, `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`strict`}</code>{`, and `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`sensitive`}</code>{` options.`}</p>
    <p>{`So instead of rendering a `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Route>`}</code>{`, just `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`useRouteMatch`}</code>{` instead:`}</p>
    <div {...{
      "className": "gatsby-highlight",
      "data-language": "jsx"
    }}><pre parentName="div" {...{
        "className": "language-jsx"
      }}><code parentName="pre" {...{
          "className": "language-jsx"
        }}><span parentName="code" {...{
            "className": "token comment"
          }}>{`// before`}</span>{`
`}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`import`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{` Route `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{` `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`from`}</span>{` `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'react-router-dom'`}</span>{`

`}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`function`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`App`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`return`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span>{`
    `}<span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span>{`div`}</span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
      `}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span><span parentName="code" {...{
            "className": "token comment"
          }}>{`/* ... */`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
      `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Route`}</span></span>{`
        `}<span parentName="span" {...{
              "className": "token attr-name"
            }}>{`path`}</span><span parentName="span" {...{
              "className": "token attr-value"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`=`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`"`}</span>{`/BLOG/:slug/`}<span parentName="span" {...{
                "className": "token punctuation"
              }}>{`"`}</span></span>{`
        `}<span parentName="span" {...{
              "className": "token attr-name"
            }}>{`strict`}</span>{`
        `}<span parentName="span" {...{
              "className": "token attr-name"
            }}>{`sensitive`}</span>{`
        `}<span parentName="span" {...{
              "className": "token attr-name"
            }}>{`render`}</span><span parentName="span" {...{
              "className": "token script language-javascript"
            }}><span parentName="span" {...{
                "className": "token script-punctuation punctuation"
              }}>{`=`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`{`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`(`}</span><span parentName="span" {...{
                "className": "token parameter"
              }}><span parentName="span" {...{
                  "className": "token punctuation"
                }}>{`{`}</span>{` match `}<span parentName="span" {...{
                  "className": "token punctuation"
                }}>{`}`}</span></span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`)`}</span>{` `}<span parentName="span" {...{
                "className": "token operator"
              }}>{`=>`}</span>{` `}<span parentName="span" {...{
                "className": "token punctuation"
              }}>{`{`}</span>{`
          `}<span parentName="span" {...{
                "className": "token keyword"
              }}>{`return`}</span>{` match `}<span parentName="span" {...{
                "className": "token operator"
              }}>{`?`}</span>{` `}<span parentName="span" {...{
                "className": "token tag"
              }}><span parentName="span" {...{
                  "className": "token tag"
                }}><span parentName="span" {...{
                    "className": "token punctuation"
                  }}>{`<`}</span><span parentName="span" {...{
                    "className": "token class-name"
                  }}>{`BlogPost`}</span></span>{` `}<span parentName="span" {...{
                  "className": "token attr-name"
                }}>{`match`}</span><span parentName="span" {...{
                  "className": "token script language-javascript"
                }}><span parentName="span" {...{
                    "className": "token script-punctuation punctuation"
                  }}>{`=`}</span><span parentName="span" {...{
                    "className": "token punctuation"
                  }}>{`{`}</span>{`match`}<span parentName="span" {...{
                    "className": "token punctuation"
                  }}>{`}`}</span></span>{` `}<span parentName="span" {...{
                  "className": "token punctuation"
                }}>{`/>`}</span></span>{` `}<span parentName="span" {...{
                "className": "token operator"
              }}>{`:`}</span>{` `}<span parentName="span" {...{
                "className": "token tag"
              }}><span parentName="span" {...{
                  "className": "token tag"
                }}><span parentName="span" {...{
                    "className": "token punctuation"
                  }}>{`<`}</span><span parentName="span" {...{
                    "className": "token class-name"
                  }}>{`NotFound`}</span></span>{` `}<span parentName="span" {...{
                  "className": "token punctuation"
                }}>{`/>`}</span></span>{`
        `}<span parentName="span" {...{
                "className": "token punctuation"
              }}>{`}`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`}`}</span></span>{`
      `}<span parentName="span" {...{
              "className": "token punctuation"
            }}>{`/>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
    `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`</`}</span>{`div`}</span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span>{`
  `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{`
`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{`

`}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// after`}</span>{`
`}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`import`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{` useRouteMatch `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{` `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`from`}</span>{` `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'react-router-dom'`}</span>{`

`}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`function`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`App`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`let`}</span>{` match `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`useRouteMatch`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
    path`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`:`}</span>{` `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'/BLOG/:slug/'`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
    strict`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`:`}</span>{` `}<span parentName="code" {...{
            "className": "token boolean"
          }}>{`true`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
    sensitive`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`:`}</span>{` `}<span parentName="code" {...{
            "className": "token boolean"
          }}>{`true`}</span>{`
  `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{`

  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`return`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span>{`
    `}<span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span>{`div`}</span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
      `}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span><span parentName="code" {...{
            "className": "token comment"
          }}>{`/* ... */`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
      `}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`match `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`?`}</span>{` `}<span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`BlogPost`}</span></span>{` `}<span parentName="span" {...{
              "className": "token attr-name"
            }}>{`match`}</span><span parentName="span" {...{
              "className": "token script language-javascript"
            }}><span parentName="span" {...{
                "className": "token script-punctuation punctuation"
              }}>{`=`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`{`}</span>{`match`}<span parentName="span" {...{
                "className": "token punctuation"
              }}>{`}`}</span></span>{` `}<span parentName="span" {...{
              "className": "token punctuation"
            }}>{`/>`}</span></span>{` `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`:`}</span>{` `}<span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`NotFound`}</span></span>{` `}<span parentName="span" {...{
              "className": "token punctuation"
            }}>{`/>`}</span></span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
    `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`</`}</span>{`div`}</span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span>{`
  `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{`
`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span></code></pre></div>
    <p>{`Just like `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Route>`}</code>{`, if you omit the `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`path`}</code>{` this hook will return the `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`match`}</code>{` from the closest matching `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Route>`}</code>{` in the tree.`}</p>
    <h2 {...{
      "id": "staying-ahead-of-the-curve"
    }}><a parentName="h2" {...{
        "href": "#staying-ahead-of-the-curve",
        "aria-hidden": "true",
        "tabIndex": -1
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`Staying Ahead of The Curve`}</h2>
    <p>{`If you'd like to get a head start on upgrading to the next major version of React Router, there are a few things you can do today assuming you're on both React >= 16.8 and React Router 5.1. A few important points here:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`All of these steps are optional`}</strong>{`. You can make zero changes to your app and use 5.1. These steps are just going to give you a bit of a head start on upgrading to React Router 6.`}</li>
      <li parentName="ul">{`You don't have to migrate all of your route components at once! `}<strong parentName="li">{`You can do just a few of them now and come back for the rest later`}</strong>{`.`}</li>
    </ul>
    <p>{`First of all, update your route components. That means changing:`}</p>
    <div {...{
      "className": "gatsby-highlight",
      "data-language": "jsx"
    }}><pre parentName="div" {...{
        "className": "language-jsx"
      }}><code parentName="pre" {...{
          "className": "language-jsx"
        }}><span parentName="code" {...{
            "className": "token comment"
          }}>{`// before`}</span>{`
`}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`function`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`BlogPost`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token parameter"
          }}><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`{`}</span>{` match`}<span parentName="span" {...{
              "className": "token punctuation"
            }}>{`,`}</span>{` location`}<span parentName="span" {...{
              "className": "token punctuation"
            }}>{`,`}</span>{` history `}<span parentName="span" {...{
              "className": "token punctuation"
            }}>{`}`}</span></span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`let`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{` slug `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{` `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span>{` match`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`.`}</span>{`params
  `}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// ...`}</span>{`
`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{`

`}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// after`}</span>{`
`}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`function`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`BlogPost`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`let`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{` slug `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{` `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`useParams`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{`
  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`let`}</span>{` location `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`useLocation`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{`
  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`let`}</span>{` history `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`useHistory`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{`
  `}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// ...`}</span>{`
`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span></code></pre></div>
    <p>{`You can do this to one component, ship it, go home and enjoy your weekend. No need to do them all at once.`}</p>
    <p>{`Then, if you're feeling ambitious, you can go ahead and update some of your `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Switch>`}</code>{` configurations to use regular `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Route children>`}</code>{` instead of `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`component`}</code>{` and/or `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`render`}</code>{` props for the route components that are now using hooks:`}</p>
    <div {...{
      "className": "gatsby-highlight",
      "data-language": "jsx"
    }}><pre parentName="div" {...{
        "className": "language-jsx"
      }}><code parentName="pre" {...{
          "className": "language-jsx"
        }}><span parentName="code" {...{
            "className": "token comment"
          }}>{`// before`}</span>{`
`}<span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Switch`}</span></span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
  `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Route`}</span></span>{` `}<span parentName="span" {...{
              "className": "token attr-name"
            }}>{`path`}</span><span parentName="span" {...{
              "className": "token attr-value"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`=`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`"`}</span>{`/blog/:slug`}<span parentName="span" {...{
                "className": "token punctuation"
              }}>{`"`}</span></span>{` `}<span parentName="span" {...{
              "className": "token attr-name"
            }}>{`component`}</span><span parentName="span" {...{
              "className": "token script language-javascript"
            }}><span parentName="span" {...{
                "className": "token script-punctuation punctuation"
              }}>{`=`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`{`}</span>{`BlogPost`}<span parentName="span" {...{
                "className": "token punctuation"
              }}>{`}`}</span></span>{` `}<span parentName="span" {...{
              "className": "token punctuation"
            }}>{`/>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
  `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Route`}</span></span>{`
    `}<span parentName="span" {...{
              "className": "token attr-name"
            }}>{`path`}</span><span parentName="span" {...{
              "className": "token attr-value"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`=`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`"`}</span>{`/posts/:slug`}<span parentName="span" {...{
                "className": "token punctuation"
              }}>{`"`}</span></span>{`
    `}<span parentName="span" {...{
              "className": "token attr-name"
            }}>{`render`}</span><span parentName="span" {...{
              "className": "token script language-javascript"
            }}><span parentName="span" {...{
                "className": "token script-punctuation punctuation"
              }}>{`=`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`{`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`(`}</span><span parentName="span" {...{
                "className": "token parameter"
              }}><span parentName="span" {...{
                  "className": "token punctuation"
                }}>{`{`}</span>{` match `}<span parentName="span" {...{
                  "className": "token punctuation"
                }}>{`}`}</span></span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`)`}</span>{` `}<span parentName="span" {...{
                "className": "token operator"
              }}>{`=>`}</span>{` `}<span parentName="span" {...{
                "className": "token tag"
              }}><span parentName="span" {...{
                  "className": "token tag"
                }}><span parentName="span" {...{
                    "className": "token punctuation"
                  }}>{`<`}</span><span parentName="span" {...{
                    "className": "token class-name"
                  }}>{`BlogPost`}</span></span>{` `}<span parentName="span" {...{
                  "className": "token attr-name"
                }}>{`match`}</span><span parentName="span" {...{
                  "className": "token script language-javascript"
                }}><span parentName="span" {...{
                    "className": "token script-punctuation punctuation"
                  }}>{`=`}</span><span parentName="span" {...{
                    "className": "token punctuation"
                  }}>{`{`}</span>{`match`}<span parentName="span" {...{
                    "className": "token punctuation"
                  }}>{`}`}</span></span>{` `}<span parentName="span" {...{
                  "className": "token attr-name"
                }}>{`other`}</span><span parentName="span" {...{
                  "className": "token attr-value"
                }}><span parentName="span" {...{
                    "className": "token punctuation"
                  }}>{`=`}</span><span parentName="span" {...{
                    "className": "token punctuation"
                  }}>{`"`}</span>{`props`}<span parentName="span" {...{
                    "className": "token punctuation"
                  }}>{`"`}</span></span>{` `}<span parentName="span" {...{
                  "className": "token punctuation"
                }}>{`/>`}</span></span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`}`}</span></span>{`
  `}<span parentName="span" {...{
              "className": "token punctuation"
            }}>{`/>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
`}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`</`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Switch`}</span></span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span>{`

`}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// after`}</span>{`
`}<span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Switch`}</span></span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
  `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Route`}</span></span>{` `}<span parentName="span" {...{
              "className": "token attr-name"
            }}>{`path`}</span><span parentName="span" {...{
              "className": "token attr-value"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`=`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`"`}</span>{`/blog/:slug`}<span parentName="span" {...{
                "className": "token punctuation"
              }}>{`"`}</span></span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
    `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`BlogPost`}</span></span>{` `}<span parentName="span" {...{
              "className": "token punctuation"
            }}>{`/>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
  `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`</`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Route`}</span></span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
  `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Route`}</span></span>{` `}<span parentName="span" {...{
              "className": "token attr-name"
            }}>{`path`}</span><span parentName="span" {...{
              "className": "token attr-value"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`=`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`"`}</span>{`/posts/:slug`}<span parentName="span" {...{
                "className": "token punctuation"
              }}>{`"`}</span></span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
    `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`BlogPost`}</span></span>{` `}<span parentName="span" {...{
              "className": "token attr-name"
            }}>{`other`}</span><span parentName="span" {...{
              "className": "token attr-value"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`=`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`"`}</span>{`props`}<span parentName="span" {...{
                "className": "token punctuation"
              }}>{`"`}</span></span>{` `}<span parentName="span" {...{
              "className": "token punctuation"
            }}>{`/>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
  `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`</`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Route`}</span></span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
`}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`</`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Switch`}</span></span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span></code></pre></div>
    <p>{`We encourage you to get used to using `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Route children>`}</code>{` instead of `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Route component>`}</code>{` and `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Route render>`}</code>{`. That way, you can enjoy using `}<em parentName="p">{`JSX`}</em>{` to compose your `}<em parentName="p">{`elements`}</em>{` and `}<em parentName="p">{`hooks`}</em>{` to compose your `}<em parentName="p">{`state`}</em>{`!`}</p>
    <p>{`Although they are not deprecated in 5.1, the `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Route component>`}</code>{` and `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Route render>`}</code>{` APIs have several quirks that just aren't needed (see the discussion in `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`useParams`}</code>{` above) in a world with hooks. We will most likely deprecate these APIs in a future release.`}</p>
    <p>{`If you'd like to do even more, go ahead and:`}</p>
    <ul>
      <li parentName="ul">{`Replace any `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`withRouter`}</code>{` usage with hooks. They give you access to all the same stuff. Again, although `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`withRouter`}</code>{` is not deprecated in 5.1, it's a weird API when you can compose your state with hooks instead. It will also most likely be deprecated in a future release.`}</li>
      <li parentName="ul">{`Replace any "floating" (not inside a `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`<Switch>`}</code>{`) `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`<Route>`}</code>{` elements with `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`useRouteMatch`}</code></li>
    </ul>
    <p>{`And ... that's it! Again, all of these steps are optional, but they should go a long way in getting your app ready for our next major version.`}</p>
    <p>{`We sincerely hope you enjoy React Router 5.1, and we look forward to announcing and shipping even more improvements very soon!`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      